/**
 * Panel Registry - Central registry mapping panel types to components
 * Provides metadata and component references for all panel types
 */

import React from 'react';
import type { PanelType } from './panel-layout-store';
import type { Tier } from './use-tier';

export interface PanelRegistryEntry {
  type: PanelType;
  title: string;
  icon: string; // Emoji for toolbar dropdown
  description: string;
  defaultSize: { width: number; height: number };
  minSize: { width: number; height: number };
  Component: React.ComponentType<any>;
  /** Minimum tier required to open this panel. Omit (or 'free') for unrestricted. */
  tier?: Tier;
  /** Hide from the panel launcher (still openable programmatically). */
  hidden?: boolean;
}

/**
 * Panel Registry - All available panel types
 * Dynamically imported to avoid circular dependencies
 */
export const PANEL_REGISTRY: Record<PanelType, PanelRegistryEntry> = {
  discord: {
    type: 'discord',
    title: 'Discord',
    icon: '💬',
    description: 'Discord timeline and messaging',
    defaultSize: { width: 500, height: 700 },
    minSize: { width: 350, height: 400 },
    hidden: true,
    Component: React.lazy(() =>
      import('./discord-timeline').then((m) => ({ default: m.DiscordTimeline }))
    ),
  },
  x: {
    type: 'x',
    title: 'X / Twitter',
    icon: '𝕏',
    description: 'X account integration - OAuth login, profile, and timeline',
    defaultSize: { width: 500, height: 700 },
    minSize: { width: 350, height: 400 },
    hidden: true,
    Component: React.lazy(() =>
      import('./x-unified-panel').then((m) => ({ default: m.XUnifiedPanel }))
    ),
  },
  '3d': {
    type: '3d',
    title: '3D View',
    icon: '🎮',
    description: '3D spatial visualization',
    defaultSize: { width: 800, height: 600 },
    minSize: { width: 400, height: 300 },
    hidden: true,
    Component: React.lazy(() =>
      import('./spatial-3d').then((m) => ({ default: m.ConnectedSpatial3D }))
    ),
  },
  'spatial-grid': {
    type: 'spatial-grid',
    title: 'Spatial Grid',
    icon: '📊',
    description: '2D spatial grid view',
    defaultSize: { width: 800, height: 600 },
    minSize: { width: 400, height: 300 },
    hidden: true,
    Component: React.lazy(() =>
      import('./spatial-components').then((m) => ({
        default: m.ConnectedSpatialGrid,
      }))
    ),
  },
  settings: {
    type: 'settings',
    title: 'Settings',
    icon: '⚙️',
    description: 'Application settings',
    defaultSize: { width: 450, height: 600 },
    minSize: { width: 300, height: 400 },
    Component: React.lazy(() =>
      import('./settings-panel').then((m) => ({ default: m.SettingsPanel }))
    ),
  },
  identity: {
    type: 'identity',
    title: 'Identity Manager',
    icon: '🔐',
    description: 'Login/logout and switch identities',
    defaultSize: { width: 500, height: 600 },
    minSize: { width: 350, height: 400 },
    Component: React.lazy(() =>
      import('./identity-manager').then((m) => ({ default: m.IdentityManagerPanel }))
    ),
  },
  'linked-identities': {
    type: 'linked-identities',
    title: 'Linked Identities',
    icon: '🔗',
    description: 'Linked identity management',
    defaultSize: { width: 500, height: 600 },
    minSize: { width: 350, height: 400 },
    hidden: true,
    Component: React.lazy(() =>
      import('./linked-identities-panel').then((m) => ({
        default: m.LinkedIdentitiesPanelWithState,
      }))
    ),
  },
  business: {
    type: 'business',
    title: 'Business',
    icon: '💼',
    description: 'Business directory',
    defaultSize: { width: 500, height: 600 },
    minSize: { width: 350, height: 400 },
    hidden: true,
    Component: React.lazy(() =>
      import('./business-panel').then((m) => ({ default: m.BusinessPanel }))
    ),
  },
  'private-chat': {
    type: 'private-chat',
    title: 'Private Messages',
    icon: '🔐',
    description: 'End-to-end encrypted messaging',
    defaultSize: { width: 500, height: 700 },
    minSize: { width: 350, height: 400 },
    Component: React.lazy(() =>
      import('./private-chat-panel').then((m) => ({ default: m.PrivateChatPanel }))
    ),
  },
  receipts: {
    type: 'receipts',
    title: 'Receipts',
    icon: '🧾',
    description: 'Receipt history and merkle proofs',
    defaultSize: { width: 650, height: 700 },
    minSize: { width: 450, height: 400 },
    hidden: true,
    Component: React.lazy(() =>
      import('./receipts-panel').then((m) => ({ default: m.ReceiptsPanel }))
    ),
  },
  metrics: {
    type: 'metrics',
    title: 'Metrics',
    icon: '📈',
    description: 'Server metrics and analytics',
    defaultSize: { width: 600, height: 700 },
    minSize: { width: 400, height: 400 },
    hidden: true,
    Component: React.lazy(() =>
      import('./metrics-panel').then((m) => ({ default: m.MetricsPanel }))
    ),
  },
  pieces: {
    type: 'pieces',
    title: 'Pieces',
    icon: '💎',
    description: 'Manage and trade pieces',
    defaultSize: { width: 500, height: 700 },
    minSize: { width: 350, height: 400 },
    hidden: true,
    Component: React.lazy(() => import('./pieces-panel').then((m) => ({ default: m.PiecesPanel }))),
  },
  troves: {
    type: 'troves',
    title: 'Troves',
    icon: '🗝️',
    description: 'Multi-party unlock puzzles',
    defaultSize: { width: 500, height: 700 },
    minSize: { width: 350, height: 400 },
    hidden: true,
    Component: React.lazy(() => import('./troves-panel').then((m) => ({ default: m.TrovesPanel }))),
  },
  canvas: {
    type: 'canvas',
    title: 'Small Blocks Canvas',
    icon: '🎨',
    description: 'Collaborative pixel canvas (250px/day)',
    defaultSize: { width: 550, height: 680 },
    minSize: { width: 400, height: 500 },
    hidden: true,
    Component: React.lazy(() => import('./canvas-panel').then((m) => ({ default: m.CanvasPanel }))),
  },
  objects: {
    type: 'objects',
    title: 'Objects',
    icon: '📦',
    description: 'Place and manage objects in spatial regions',
    defaultSize: { width: 600, height: 700 },
    minSize: { width: 400, height: 400 },
    hidden: true,
    Component: React.lazy(() =>
      import('./objects-panel').then((m) => ({ default: m.ObjectsPanel }))
    ),
  },
  'world-map': {
    type: 'world-map',
    title: 'World Map',
    icon: '🌍',
    description: 'Global user map with live tracking',
    defaultSize: { width: 900, height: 700 },
    minSize: { width: 600, height: 500 },
    hidden: true,
    Component: React.lazy(() =>
      import('./world-map-panel').then((m) => ({ default: m.WorldMapPanel }))
    ),
  },
  voice: {
    type: 'voice',
    title: 'Voice Chat',
    icon: '🎤',
    description: 'Voice chat with speaking indicators and live captions',
    defaultSize: { width: 600, height: 700 },
    minSize: { width: 400, height: 500 },
    Component: React.lazy(() => import('./voice-panel').then((m) => ({ default: m.VoicePanel }))),
  },
  'my-accords': {
    type: 'my-accords',
    title: 'My Accords',
    icon: '📋',
    description: 'Manage your contextual profiles and integrations',
    defaultSize: { width: 700, height: 800 },
    minSize: { width: 500, height: 600 },
    hidden: true,
    Component: React.lazy(() =>
      import('./my-profile-page').then((m) => ({ default: m.MyProfilePage }))
    ),
  },
  'public-profile': {
    type: 'public-profile',
    title: 'Public Profile',
    icon: '🌐',
    description: 'View public profiles',
    defaultSize: { width: 600, height: 750 },
    minSize: { width: 400, height: 500 },
    hidden: true,
    Component: React.lazy(() =>
      import('./public-profile-view').then((m) => ({ default: m.PublicProfileView }))
    ),
  },
  'accord-detail': {
    type: 'accord-detail',
    title: 'Accord',
    icon: '📖',
    description: 'View individual accord details',
    defaultSize: { width: 500, height: 700 },
    minSize: { width: 350, height: 500 },
    hidden: true,
    Component: React.lazy(() =>
      import('./public-accord-card').then((m) => ({ default: m.PublicAccordCard }))
    ),
  },
  preferences: {
    type: 'preferences',
    title: 'Preferences',
    icon: '💾',
    description: 'SMT-backed user preferences with sync',
    defaultSize: { width: 600, height: 700 },
    minSize: { width: 400, height: 500 },
    Component: React.lazy(() =>
      import('./preferences-settings-panel').then((m) => ({
        default: m.PreferencesSettingsPanel,
      }))
    ),
  },
  groups: {
    type: 'groups',
    title: 'Groups',
    icon: '👥',
    description: 'Identity-based communities (adjectives and nouns)',
    defaultSize: { width: 600, height: 750 },
    minSize: { width: 450, height: 600 },
    Component: React.lazy(() => import('./groups-panel').then((m) => ({ default: m.GroupsPanel }))),
  },
  feed: {
    type: 'feed',
    title: 'Rising Sun Feed',
    icon: '🌅',
    description: 'SMT-verified posts with visibility controls',
    defaultSize: { width: 550, height: 750 },
    minSize: { width: 350, height: 500 },
    Component: React.lazy(() => import('./feed/feed-panel')),
  },
  topics: {
    type: 'topics',
    title: 'Topics',
    icon: '🌐',
    description: 'Topic-first social — type what you want, see who is active',
    defaultSize: { width: 550, height: 750 },
    minSize: { width: 350, height: 500 },
    Component: React.lazy(() =>
      import('./topic-browser/topic-browser').then((m) => ({ default: m.TopicBrowser }))
    ),
  },
  notes: {
    type: 'notes',
    title: 'Notes',
    icon: '📝',
    description: 'Personal notes — create, tag, link, and plant notes on the floor',
    defaultSize: { width: 480, height: 680 },
    minSize: { width: 320, height: 400 },
    Component: React.lazy(() =>
      import('./notes-panel').then((m) => ({ default: m.NotesPanel }))
    ),
  },
  'meeting-notes': {
    type: 'meeting-notes',
    title: 'Meeting Notes',
    icon: '📋',
    description: 'Structured note taker for meetings — agenda, notes, decisions, action items',
    defaultSize: { width: 520, height: 720 },
    minSize: { width: 380, height: 500 },
    // Panel itself is free; LLM features inside gate on 'premium' via useTierCheck
    tier: 'free',
    hidden: false,
    Component: React.lazy(() =>
      import('./meeting-notes-panel').then((m) => ({ default: m.MeetingNotesPanel }))
    ),
  },
  'avid-player': {
    type: 'avid-player',
    title: 'AVID Player',
    icon: '🎬',
    description: 'Watch an AVID briefing — keyframes, narration, and live co-watchers',
    defaultSize: { width: 520, height: 680 },
    minSize: { width: 380, height: 480 },
    Component: React.lazy(() =>
      import('./avid/avid-player-panel').then((m) => ({ default: m.AVIDPlayerPanel }))
    ),
  },
  'avid-channels': {
    type: 'avid-channels',
    title: 'AVID Channels',
    icon: '📡',
    description: 'Browse agent-authored AVID channels — trending briefings, subscriptions',
    defaultSize: { width: 480, height: 700 },
    minSize: { width: 340, height: 480 },
    Component: React.lazy(() =>
      import('./avid/avid-channel-panel').then((m) => ({ default: m.AVIDChannelPanel }))
    ),
  },
  'avid-publish': {
    type: 'avid-publish',
    title: 'Publish AVID',
    icon: '📤',
    description: 'Author an AVID bundle and publish it to your channel',
    defaultSize: { width: 460, height: 720 },
    minSize: { width: 360, height: 480 },
    Component: React.lazy(() =>
      import('./avid/avid-publish-panel').then((m) => ({ default: m.AVIDPublishPanel }))
    ),
  },
  'review-receipt': {
    type: 'review-receipt',
    title: 'Project Review',
    icon: '✓',
    description: 'Stamp cryptographic receipts for project reviews',
    defaultSize: { width: 560, height: 680 },
    minSize: { width: 400, height: 500 },
    Component: React.lazy(() =>
      import('./review-receipt-panel').then((m) => ({ default: m.ReviewReceiptPanel }))
    ),
  },
};

/**
 * Get registry entry for a panel type
 */
export function getPanelEntry(type: PanelType): PanelRegistryEntry {
  const entry = PANEL_REGISTRY[type];
  if (!entry) {
    console.warn(`Unknown panel type: ${type} — skipping`);
    // Return a safe fallback so stale persisted layouts don't crash the app
    return {
      type,
      title: type,
      icon: '❓',
      description: '',
      defaultSize: { width: 400, height: 400 },
      minSize: { width: 200, height: 200 },
      hidden: true,
      Component: React.lazy(() => Promise.resolve({ default: () => null })),
    };
  }
  return entry;
}

/**
 * List all available panel types
 */
export function listPanelTypes(): PanelType[] {
  return Object.keys(PANEL_REGISTRY) as PanelType[];
}
